import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

// Animation constants (extracted to prevent inline object creation on each render)
const FADE_UP_INITIAL = { opacity: 0, y: 40 };
const FADE_UP_ANIMATE = { opacity: 1, y: 0 };
const FADE_UP_EMPTY = {};
const FADE_UP_HEADER_INITIAL = { opacity: 0, y: 30 };
const FADE_IN_INITIAL = { opacity: 0 };
const FADE_IN_ANIMATE = { opacity: 1 };
const HEADER_TRANSITION = { duration: 0.6 };
const FOOTER_TRANSITION = { duration: 0.6, delay: 0.6 };
const VIEW_MARGIN = { once: true, margin: "-100px" };

const services = [
  {
    id: "remodel",
    name: "Full Remodels",
    description: "Complete home renovation and remodeling - our specialty",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/1vf4qovi_kiten%20remodel.jpg",
    span: "md:col-span-4 lg:col-span-6"
  },
  {
    id: "stucco",
    name: "Stucco",
    description: "Professional stucco application and repair",
    image: "https://images.pexels.com/photos/4913793/pexels-photo-4913793.jpeg",
    span: "md:col-span-4 lg:col-span-6"
  },
  {
    id: "tile",
    name: "Tile & Finishing",
    description: "Expert tile installation and finishing work",
    image: "https://images.pexels.com/photos/16501255/pexels-photo-16501255.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-4 lg:col-span-4"
  },
  {
    id: "concrete",
    name: "Concrete",
    description: "Professional concrete pouring, finishing, and flatwork",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/a0dillsn_concreate.jpg",
    span: "md:col-span-4 lg:col-span-4"
  },
  {
    id: "stone",
    name: "Stone Masonry",
    description: "Custom stonework, fireplaces, and masonry projects",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/w2qu0oah_stone.avif",
    span: "md:col-span-4 lg:col-span-4"
  },
  {
    id: "painting",
    name: "Painting",
    description: "Interior and exterior painting services",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/riz617x4_Untitled%20design%20%282%29.png",
    span: "md:col-span-12 lg:col-span-12"
  }
];

function ServiceCard({ service, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, VIEW_MARGIN);

  return (
    <motion.div
      ref={ref}
      initial={FADE_UP_INITIAL}
      animate={isInView ? FADE_UP_ANIMATE : FADE_UP_EMPTY}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`bento-item ${service.span} h-[300px] md:h-[350px] cursor-pointer group`}
      data-testid={`service-${service.id}`}
    >
      <img
        src={service.image}
        alt={service.name}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
        <h3 className="font-['Outfit'] text-xl sm:text-2xl font-medium text-white mb-2">
          {service.name}
        </h3>
        <p className="text-white/70 text-sm leading-relaxed">
          {service.description}
        </p>
      </div>
    </motion.div>
  );
}

export default function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, VIEW_MARGIN);

  return (
    <section
      id="services"
      className="py-24 md:py-32 bg-[#FAFAF9]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={FADE_UP_HEADER_INITIAL}
          animate={isInView ? FADE_UP_ANIMATE : FADE_UP_EMPTY}
          transition={HEADER_TRANSITION}
          className="mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57534E] mb-4 block">
            What We Do
          </span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-[#1C1917]">
            Our Services
          </h2>
        </motion.div>

        <div
          className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4"
          data-testid="services-grid"
        >
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>

        <motion.p
          initial={FADE_IN_INITIAL}
          animate={isInView ? FADE_IN_ANIMATE : FADE_UP_EMPTY}
          transition={FOOTER_TRANSITION}
          className="text-center text-[#57534E] mt-12"
        >
          <span className="text-[#166534] font-medium">We do a lot more!</span> We know most construction trades. 
          Don't see what you need? Send us a request and we'll let you know if we can help.
        </motion.p>
      </div>
    </section>
  );
}
